import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { markerPin_04Line } from "@tql/ui-components-react/untitled/icons";
import { Link } from "react-router";

import { CopyQuoteButton } from "~/shared/components/trax-grid/copy-quote-cell/copy-quote-button.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";
import { sendQuoteBoardEditQuoteIdEvent } from "~/shared/utils/google-analytics-helper.ts";

import { useQuoteStatusAction } from "../hooks/use-quote-status-action.ts";
import type { QuoteBoardCarrierAction } from "../utils/quote-board-status.ts";
import {
	getQuoteBoardQuoteUrl,
	isQuoteBoardSelectionEligible,
} from "../utils/quote-board-status.ts";
import styles from "./quote-board-card.module.css";
import { getQuoteBoardCardFields } from "./quote-board-card-fields.ts";

type QuoteBoardCardProps = {
	item: QuoteBoardItem | CustomerQuoteBoardItem;
	isInternal?: boolean;
	onOpenCarrierSelection?: (quoteId: number) => void;
	onOpenBulkCarrierSelection?: (quoteId: number) => void;
	showBulkTenderSelection?: boolean;
	isSelected?: boolean;
	onToggleSelection?: (quoteId: number, selected: boolean) => void;
};

const Metric = ({
	label,
	value,
	wrap = false,
}: {
	label: string;
	value: string;
	wrap?: boolean;
}) => (
	<div className={styles.metric}>
		<span className={styles.metricLabel}>{label}</span>
		<span
			className={
				wrap
					? `${styles.metricValue} ${styles.metricValueWrap}`
					: styles.metricValue
			}
		>
			{value}
		</span>
	</div>
);

const CarrierMetric = ({
	carrierName,
	carrierAction,
	onChange,
}: {
	carrierName: string | null;
	carrierAction: QuoteBoardCarrierAction;
	onChange: () => void;
}) => (
	<div className={styles.metric}>
		<span className={styles.metricLabel}>Carrier Name</span>
		<span className={styles.metricValue}>{carrierName ?? "\u2014"}</span>
		{carrierAction === "none" ? null : (
			<div className={styles.carrierChangeButton}>
				<Button onClick={onChange} variant={"secondary"}>
					{carrierAction === "change" ? "Change" : "Select"}
				</Button>
			</div>
		)}
	</div>
);

export const QuoteBoardCard = ({
	item,
	isInternal = false,
	onOpenCarrierSelection,
	onOpenBulkCarrierSelection,
	showBulkTenderSelection = false,
	isSelected = false,
	onToggleSelection,
}: QuoteBoardCardProps) => {
	const fields = getQuoteBoardCardFields(item, onOpenBulkCarrierSelection);
	const status = useQuoteStatusAction({
		dataItem: item,
		isInternal,
		onOpenCarrierSelection,
	});
	// Mobile card abbreviates the shared "Select a Carrier" status per the wireframe.
	const statusLabel =
		status.label === "Select a Carrier" ? "Select Carrier" : status.label;
	const quoteUrl = getQuoteBoardQuoteUrl(item, { isInternal });
	const isSelectionEligible = isQuoteBoardSelectionEligible(item, {
		isInternal,
	});

	return (
		<article
			className={styles.card}
			data-quote-id={item.quoteId}
			data-testid={"quote-board-card"}
		>
			<div className={styles.topRow}>
				<div className={styles.idGroup}>
					{showBulkTenderSelection ? (
						<Checkbox
							aria-label={`Select quote ${item.quoteId}`}
							checked={isSelected}
							disabled={!isSelectionEligible}
							onChange={(event) =>
								onToggleSelection?.(item.quoteId, event.value)
							}
						/>
					) : null}
					{quoteUrl ? (
						<Link
							className={styles.quoteLink}
							data-testid={"quote-board-card-link"}
							onClick={() =>
								sendQuoteBoardEditQuoteIdEvent(String(item.quoteId))
							}
							to={quoteUrl}
						>
							{item.quoteId}
						</Link>
					) : (
						<span className={styles.quoteLink}>{item.quoteId}</span>
					)}
					<span className={styles.shipDate}>Ship: {fields.shipDate}</span>
				</div>
				<div
					className={status.className}
					data-testid={"quote-board-card-status"}
				>
					<Button
						disabled={status.disabled}
						onClick={status.handleClick}
						title={status.title}
						variant={status.isError ? "dangerSecondary" : "secondary"}
					>
						{statusLabel}
					</Button>
				</div>
			</div>

			<div className={styles.infoSection}>
				<div className={styles.locations}>
					<div className={styles.locationRow}>
						<SvgIcon className={styles.locationIcon} icon={markerPin_04Line} />
						<span>{fields.originAddress}</span>
					</div>
					<div className={styles.locationRow}>
						<SvgIcon className={styles.locationIcon} icon={markerPin_04Line} />
						<span>{fields.destinationAddress}</span>
					</div>
				</div>
				<div className={styles.sideMetrics}>
					<Metric label={"Created"} value={fields.createdDate} />
				</div>
			</div>

			<div className={styles.metricGrid}>
				<Metric label={"Weight"} value={fields.weight} />
				<CarrierMetric
					carrierAction={fields.carrierAction}
					carrierName={fields.carrierName}
					onChange={() => onOpenBulkCarrierSelection?.(item.quoteId)}
				/>
				<Metric wrap label={"Max Liability"} value={fields.maxLiability} />
				<Metric label={"Rate"} value={fields.rate} />
			</div>

			<div className={styles.referenceRow}>
				<span className={styles.metricLabel}>Customer Reference</span>
				<span>{fields.customerReference}</span>
			</div>

			<div className={styles.footer}>
				<span className={styles.submittedBy}>By: {fields.submittedBy}</span>
				<div className={styles.copyQuoteButton}>
					<CopyQuoteButton quoteId={item.quoteId} />
				</div>
			</div>

			{status.isErrorModalOpen ? (
				<TraxModal
					onCancel={status.closeErrorModal}
					onClose={status.closeErrorModal}
					showConfirmationButtons={false}
					title={status.errorModalTitle}
					type={"error"}
					width={"90%"}
				>
					<div className={"bulk-tender-error-content"}>
						<p data-testid={"bulk-upload-exception-message"}>
							{status.errorMessage}
						</p>
						{status.showCopyQuoteInErrorModal ? (
							<div className={"bulk-tender-error-action"}>
								<CopyQuoteButton quoteId={status.quoteId} showLabel={true} />
							</div>
						) : null}
					</div>
				</TraxModal>
			) : null}
		</article>
	);
};
